import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  Clock,
  Bot,
  Terminal,
  Zap,
  ChevronRight,
  CheckCircle,
} from "lucide-react";

const initialFormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
  company: "",
};

const ContactSection = () => {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    company: "",
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value,
    });
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (formStatus.isSubmitting) return;
  //   setFormStatus({ ...formStatus, isSubmitting: true });

  //   try {
  //     // Send the form data to your Make webhook
  //     const response = await fetch(
  //       // "https://aaagentive.app.n8n.cloud/webhook-test/df9b4d39-a5d9-4da1-84f6-c774d523e2ab",
  //       "https://n8n-aaagentive.onrender.com/webhook-test/218bc98e-c274-4618-ad20-aba0a142ebd3",
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(formState),
  //       }
  //     );

  //     if (response.ok) {
  //       // Show success message
  //       setFormStatus({ isSubmitting: false, isSubmitted: true });
  //       // Clear the form
  //       setFormState(initialFormState);
  //     } else {
  //       throw new Error("Form submission failed");
  //     }
  //   } catch (error) {
  //     console.error("Error submitting form:", error);
  //     // Handle error (add appropriate error state/messaging)
  //     setFormStatus({ isSubmitting: false, isSubmitted: false });
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submission if already submitting
    if (formStatus.isSubmitting) return;

    // Add timestamp and unique ID to help identify duplicate submissions
    const submissionId =
      Date.now().toString(36) + Math.random().toString(36).substr(2);
    const submissionData = {
      ...formState,
      submissionId,
      timestamp: new Date().toISOString(),
    };

    // Set submitting state immediately
    setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

    try {
      // Add a small artificial delay to prevent accidental double clicks
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Send the form data to your n8n webhook
      const response = await fetch(
        "https://n8n-aaagentive.onrender.com/webhook/218bc98e-c274-4618-ad20-aba0a142ebd3",

        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
          // Add these options to prevent automatic retries on certain errors
          cache: "no-store",
          keepalive: true,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Form submission failed with status: ${response.status}`
        );
      }

      // Show success message and reset form
      setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });
      setFormState(initialFormState);

      // Store submission ID in localStorage to prevent resubmission on page refresh
      localStorage.setItem("lastFormSubmission", submissionId);
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        error: error.message || "Failed to submit the form. Please try again.",
      });
    }
  };

  // Add this in your component, before the form handling functions
  useEffect(() => {
    // Check if we're coming back after a successful submission (e.g., after page refresh)
    const lastSubmission = localStorage.getItem("lastFormSubmission");
    if (
      lastSubmission &&
      window.performance &&
      performance.navigation.type === 1
    ) {
      // This is a page reload after submission
      setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });
    }
  }, []);

  return (
    <section
      id="contact"
      className="py-24 mt-16 mb-16 bg-white rounded-2xl overflow-hidden"
    >
      <div className="w-full max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-2 font-mono">
              Get in Touch
            </h2>
            <div className="w-16 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-gray-600">
              Let's discuss how my expertise in AI integration and software
              development can help your business achieve its goals
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Side - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-3 relative"
          >
            {/* Decorative background elements */}
            <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-blue-50 filter blur-3xl opacity-30 z-0"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-indigo-50 filter blur-3xl opacity-30 z-0"></div>

            <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden z-10">
              {/* Terminal-like header */}
              <div className="bg-black px-4 py-2 flex items-center">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="ml-4 text-xs text-gray-400 font-mono">
                  contact@tresorkl.com ~ message.js
                </div>
              </div>

              <div className="p-6">
                {!formStatus.isSubmitted ? (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                      <div className="mb-2 flex items-center">
                        <Terminal className="w-4 h-4 text-blue-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700 font-mono">
                          message.send()
                        </span>
                      </div>
                      <div className="border-b border-gray-200 w-full mb-6"></div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Name
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formState.name}
                              onChange={handleChange}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors pl-8"
                              required
                            />
                            <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400">
                              <span className="font-mono text-xs">$</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Email
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formState.email}
                              onChange={handleChange}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors pl-8"
                              required
                            />
                            <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400">
                              <span className="font-mono text-xs">@</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <label
                          htmlFor="company"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Company
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formState.company}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors pl-8"
                          />
                          <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400">
                            <span className="font-mono text-xs">&gt;</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <label
                          htmlFor="subject"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Subject
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          value={formState.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                          required
                        >
                          <option value="" disabled>
                            Select a subject
                          </option>
                          <option value="general">General Inquiry</option>
                          <option value="Web Development Project">
                            Web Development Project
                          </option>
                          <option value="Mobile App Development">
                            Mobile App Development
                          </option>
                          <option value="AI & Automation">
                            AI & Automation
                          </option>
                          <option value="consulting">
                            Technical Consulting
                          </option>
                        </select>
                      </div>

                      <div className="mt-6">
                        <label
                          htmlFor="message"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Message
                        </label>
                        <div className="relative">
                          <div className="absolute top-3 left-3 flex flex-col items-center">
                            <div className="w-px h-full bg-gray-200"></div>
                          </div>
                          <textarea
                            id="message"
                            name="message"
                            value={formState.message}
                            onChange={handleChange}
                            rows={6}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors pl-8"
                            placeholder="Tell me about your project..."
                            required
                          ></textarea>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row justify-between md:items-center">
                      <div className="text-xs text-gray-500 mb-4 md:mb-0 flex items-center">
                        <Bot className="w-4 h-4 mr-1 text-blue-500" />
                        <span>
                          Your message will be processed with speed and
                          precision
                        </span>
                      </div>

                      <button
                        type="submit"
                        className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none font-mono text-sm transition-all relative overflow-hidden group"
                        disabled={formStatus.isSubmitting}
                      >
                        {formStatus.isSubmitting ? (
                          <>
                            <span className="mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <span className="relative z-10">
                              send_message()
                            </span>
                            <div className="absolute inset-0 h-full w-0 bg-blue-600 transition-all duration-300 group-hover:w-full"></div>
                            <Send className="w-4 h-4 ml-2 relative z-10" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="p-8 text-center">
                    <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                      <div className="absolute -inset-1 rounded-full bg-green-100 animate-ping opacity-75"></div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Message Sent Successfully
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for reaching out. I'll respond to your inquiry
                      within 24 hours.
                    </p>
                    <div className="inline-flex justify-center items-center">
                      <span className="inline-flex h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                      <span className="text-sm text-gray-500 font-mono">
                        Message delivered to inbox
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Right Side - Contact Info & Quick Links */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            {/* Contact Methods Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
              <h3 className="font-mono text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Terminal className="w-5 h-5 text-blue-500 mr-2" />
                contact.methods
              </h3>

              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-50 text-blue-600">
                      <Mail className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-base font-medium text-gray-900">
                      Email
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Reach out anytime for inquiries
                    </p>
                    <a
                      href="mailto:tresorlundimu@gmail.com"
                      className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-500 font-mono"
                    >
                      tresorlundimu@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-50 text-blue-600">
                      <Phone className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-base font-medium text-gray-900">
                      Phone
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Mon-Fri from 9am to 5pm SAST
                    </p>
                    <a
                      href="tel:+27672348817"
                      className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-500 font-mono"
                    >
                      +27 67 234 8817
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ & Location Cards */}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-mono text-md font-semibold text-gray-900 mb-4">
                  Quick Links
                </h3>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="https://github.com/yourusername"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      <div className="mr-3 h-8 w-8 rounded-md bg-gray-50 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                        <svg
                          className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                        </svg>
                      </div>
                      <span className="text-sm">GitHub Projects</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://linkedin.com/in/yourusername"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      <div className="mr-3 h-8 w-8 rounded-md bg-gray-50 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                        <svg
                          className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      </div>
                      <span className="text-sm">LinkedIn Profile</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://aaagentive.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      <div className="mr-3 h-8 w-8 rounded-md bg-gray-50 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                        <Bot className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                      <span className="text-sm">AAAGentive Website</span>
                    </a>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-mono text-md font-semibold text-gray-900 mb-4">
                  Location
                </h3>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-md bg-gray-50 text-gray-500">
                      <MapPin className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-500">
                      Gauteng, South Africa
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      Remote-first with local presence
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-md bg-gray-50 text-gray-500">
                        <Clock className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Business Hours</p>
                      <p className="mt-1 text-xs text-gray-400">
                        Monday-Friday: 9am - 5pm SAST
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 p-8 md:p-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E\")",
            }}
          ></div>

          {/* Abstract Shapes */}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 rounded-full bg-white opacity-10 z-0"></div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 rounded-full bg-white opacity-10 z-0"></div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-8 md:mb-0 md:mr-8">
              <h3 className="text-2xl font-bold mb-4 font-mono">
                Ready for the next big project?
              </h3>
              <p className="text-white text-opacity-90">
                Whether you need AI integration, custom software, or technical
                consultation, I'm here to help turn your vision into reality.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:tresorlundimu@gmail.com"
                className="px-6 py-3 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </a>
              <a
                href="tel:+27672348817"
                className="px-6 py-3 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors inline-flex items-center justify-center"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
